<script setup lang="ts">
import { ArrowLeft, Camera, Check, Circle, ImagePlus, RotateCcw, Send, Sparkles, Square, Video } from 'lucide-vue-next';
import { computed, nextTick, onBeforeUnmount, ref } from 'vue';
import { useFamily } from '@/composables/useFamily';
import { useNavigation } from '@/composables/useNavigation';

type ViewState = 'choose' | 'photo-camera' | 'photo-edit' | 'video-camera' | 'video-recording' | 'video-preview' | 'sent';
type StickerKey = 'glasses' | 'moustache' | 'necklace';

const MAX_VIDEO_SECONDS = 10;

const stickerOptions: Array<{ key: StickerKey; label: string; className: string }> = [
  { key: 'glasses', label: 'Brille', className: 'sticker-choice-glasses' },
  { key: 'moustache', label: 'Schnurbart', className: 'sticker-choice-moustache' },
  { key: 'necklace', label: 'Kette', className: 'sticker-choice-necklace' }
];

const { addPendingCameraMessage, selectedFriend } = useFamily();
const { navigate } = useNavigation();

const viewState = ref<ViewState>('choose');
const cameraPreview = ref<HTMLVideoElement | null>(null);
const videoPreview = ref<HTMLVideoElement | null>(null);
const stream = ref<MediaStream | null>(null);
const recorder = ref<MediaRecorder | null>(null);
const photoUrl = ref('');
const videoUrl = ref('');
const videoBlob = ref<Blob | null>(null);
const sentUrl = ref('');
const selectedStickers = ref<StickerKey[]>([]);
const seconds = ref(0);
const errorText = ref('');

let chunks: BlobPart[] = [];
let timerId = 0;
let discardNextVideo = false;

const friendName = computed(() => selectedFriend.value?.displayName ?? 'Freund');
const videoProgress = computed(() => Math.min(100, (seconds.value / MAX_VIDEO_SECONDS) * 100));

onBeforeUnmount(() => {
  cleanupCamera();
  revokeUnsavedUrls();
});

async function choosePhoto() {
  resetMedia();
  await startCamera({ video: true, audio: false }, 'photo-camera');
}

async function chooseVideo() {
  resetMedia();
  await startCamera({ video: true, audio: true }, 'video-camera');
}

async function startCamera(constraints: MediaStreamConstraints, nextState: ViewState) {
  errorText.value = '';

  try {
    stream.value = await navigator.mediaDevices.getUserMedia(constraints);
    viewState.value = nextState;
    await nextTick();
    if (cameraPreview.value) {
      cameraPreview.value.srcObject = stream.value;
      await cameraPreview.value.play().catch(() => undefined);
    }
  } catch {
    errorText.value = 'Kamera konnte nicht gestartet werden.';
    viewState.value = 'choose';
    cleanupCamera();
  }
}

function capturePhoto() {
  const video = cameraPreview.value;
  if (!video || video.videoWidth === 0 || video.videoHeight === 0) return;

  const canvas = document.createElement('canvas');
  const maxWidth = 960;
  const scale = Math.min(1, maxWidth / video.videoWidth);
  canvas.width = Math.round(video.videoWidth * scale);
  canvas.height = Math.round(video.videoHeight * scale);
  const context = canvas.getContext('2d');
  if (!context) return;

  context.drawImage(video, 0, 0, canvas.width, canvas.height);
  revokePhotoUrl();
  photoUrl.value = canvas.toDataURL('image/png');
  selectedStickers.value = [];
  cleanupCamera();
  viewState.value = 'photo-edit';
}

function toggleSticker(sticker: StickerKey) {
  selectedStickers.value = selectedStickers.value.includes(sticker)
    ? selectedStickers.value.filter((entry) => entry !== sticker)
    : [...selectedStickers.value, sticker];
}

async function sendPhoto() {
  if (!photoUrl.value || !selectedFriend.value) return;
  const finalPhotoUrl = await createStickeredPhoto();
  sentUrl.value = finalPhotoUrl;
  await addPendingCameraMessage(selectedFriend.value.friendChildId, 'photo', dataUrlToBlob(finalPhotoUrl), selectedStickers.value);
  viewState.value = 'sent';
}

function startVideoRecording() {
  if (!stream.value || viewState.value === 'video-recording') return;

  chunks = [];
  discardNextVideo = false;
  seconds.value = 0;
  errorText.value = '';

  const nextRecorder = new MediaRecorder(stream.value);
  recorder.value = nextRecorder;

  nextRecorder.addEventListener('dataavailable', (event) => {
    if (event.data.size > 0) chunks.push(event.data);
  });

  nextRecorder.addEventListener('stop', () => {
    if (discardNextVideo) {
      discardNextVideo = false;
      recorder.value = null;
      cleanupCamera();
      stopTimer();
      return;
    }

    const blob = new Blob(chunks, { type: nextRecorder.mimeType || 'video/webm' });
    revokeVideoUrl();
    videoBlob.value = blob;
    videoUrl.value = URL.createObjectURL(blob);
    recorder.value = null;
    cleanupCamera();
    stopTimer();
    viewState.value = 'video-preview';
  });

  nextRecorder.start();
  viewState.value = 'video-recording';
  startTimer();
}

function stopVideoRecording() {
  if (!recorder.value || recorder.value.state === 'inactive') return;
  recorder.value.stop();
}

async function sendVideo() {
  if (!videoUrl.value || !selectedFriend.value || !videoBlob.value) return;
  sentUrl.value = videoUrl.value;
  await addPendingCameraMessage(selectedFriend.value.friendChildId, 'video', videoBlob.value);
  viewState.value = 'sent';
}

function resetAll() {
  discardNextVideo = true;
  cleanupCamera();
  resetMedia();
  viewState.value = 'choose';
}

function returnToFriend() {
  navigate('friend');
}

function startTimer() {
  stopTimer();
  timerId = window.setInterval(() => {
    seconds.value = Math.min(MAX_VIDEO_SECONDS, seconds.value + 1);
    if (seconds.value >= MAX_VIDEO_SECONDS) {
      stopVideoRecording();
    }
  }, 1000);
}

function stopTimer() {
  if (!timerId) return;
  window.clearInterval(timerId);
  timerId = 0;
}

function cleanupCamera() {
  if (recorder.value && recorder.value.state !== 'inactive') {
    recorder.value.stop();
  }
  stopTimer();
  if (stream.value) {
    stream.value.getTracks().forEach((track) => track.stop());
    stream.value = null;
  }
  if (cameraPreview.value) {
    cameraPreview.value.srcObject = null;
  }
}

function resetMedia() {
  revokeUnsavedUrls();
  selectedStickers.value = [];
  seconds.value = 0;
  errorText.value = '';
  chunks = [];
}

function revokeUnsavedUrls() {
  if (photoUrl.value.startsWith('blob:') && photoUrl.value !== sentUrl.value) {
    URL.revokeObjectURL(photoUrl.value);
  }
  if (videoUrl.value.startsWith('blob:') && videoUrl.value !== sentUrl.value) {
    URL.revokeObjectURL(videoUrl.value);
  }
  photoUrl.value = '';
  videoUrl.value = '';
  videoBlob.value = null;
}

function revokePhotoUrl() {
  if (photoUrl.value.startsWith('blob:') && photoUrl.value !== sentUrl.value) {
    URL.revokeObjectURL(photoUrl.value);
  }
  photoUrl.value = '';
}

function revokeVideoUrl() {
  if (videoUrl.value.startsWith('blob:') && videoUrl.value !== sentUrl.value) {
    URL.revokeObjectURL(videoUrl.value);
  }
  videoUrl.value = '';
  videoBlob.value = null;
}

function createStickeredPhoto() {
  return new Promise<string>((resolve) => {
    const image = new Image();
    image.addEventListener('load', () => {
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      const context = canvas.getContext('2d');
      if (!context) {
        resolve(photoUrl.value);
        return;
      }

      context.drawImage(image, 0, 0);
      drawStickerShapes(context, canvas.width, canvas.height, selectedStickers.value);
      resolve(canvas.toDataURL('image/png'));
    });
    image.src = photoUrl.value;
  });
}

function dataUrlToBlob(dataUrl: string) {
  const [header, base64] = dataUrl.split(',');
  const mimeType = header.match(/data:(.*);base64/)?.[1] ?? 'image/png';
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], { type: mimeType });
}

function drawStickerShapes(context: CanvasRenderingContext2D, width: number, height: number, stickers: StickerKey[]) {
  context.lineCap = 'round';
  context.lineJoin = 'round';

  if (stickers.includes('glasses')) {
    const centerX = width * 0.5;
    const centerY = height * 0.37;
    const lensWidth = width * 0.13;
    const lensHeight = height * 0.08;
    const gap = width * 0.065;
    context.strokeStyle = '#111827';
    context.lineWidth = Math.max(6, width * 0.012);
    context.strokeRect(centerX - gap - lensWidth, centerY - lensHeight / 2, lensWidth, lensHeight);
    context.strokeRect(centerX + gap, centerY - lensHeight / 2, lensWidth, lensHeight);
    context.beginPath();
    context.moveTo(centerX - gap, centerY);
    context.lineTo(centerX + gap, centerY);
    context.stroke();
  }

  if (stickers.includes('moustache')) {
    const centerX = width * 0.5;
    const centerY = height * 0.55;
    context.fillStyle = '#111827';
    context.beginPath();
    context.ellipse(centerX - width * 0.045, centerY, width * 0.07, height * 0.028, -0.15, 0, Math.PI * 2);
    context.ellipse(centerX + width * 0.045, centerY, width * 0.07, height * 0.028, 0.15, 0, Math.PI * 2);
    context.fill();
  }

  if (stickers.includes('necklace')) {
    const centerX = width * 0.5;
    const centerY = height * 0.76;
    const radius = Math.max(5, width * 0.014);
    context.fillStyle = '#f59e0b';
    for (let index = -5; index <= 5; index += 1) {
      const x = centerX + index * radius * 2.3;
      const y = centerY + Math.abs(index) * radius * 0.55;
      context.beginPath();
      context.arc(x, y, radius, 0, Math.PI * 2);
      context.fill();
    }
  }
}
</script>

<template>
  <section class="mx-auto max-w-6xl px-4 py-6 pb-28 sm:px-6 md:pb-10">
    <button class="focus-ring inline-flex min-h-12 items-center gap-2 rounded-full bg-white px-5 font-black text-heart-ink shadow-sm" type="button" @click="navigate('friend')">
      <ArrowLeft class="h-5 w-5" aria-hidden="true" />
      Zurueck
    </button>

    <div class="mt-6 rounded-[34px] bg-white p-5 text-center shadow-soft md:p-8">
      <div class="mx-auto grid h-20 w-20 place-items-center rounded-[26px] bg-emerald-100 text-emerald-800">
        <Video class="h-12 w-12" aria-hidden="true" />
      </div>
      <h1 class="mt-4 text-4xl font-black text-heart-berry md:text-6xl">Kamera fuer {{ friendName }}</h1>

      <div v-if="viewState === 'choose'" class="mt-8 grid gap-5 md:grid-cols-2">
        <button class="focus-ring camera-choice bg-sky-100 text-sky-800" type="button" aria-label="Foto aufnehmen" @click="choosePhoto">
          <Camera class="h-20 w-20 md:h-24 md:w-24" aria-hidden="true" />
          <span>Foto</span>
        </button>
        <button class="focus-ring camera-choice bg-emerald-100 text-emerald-800" type="button" aria-label="Video aufnehmen" @click="chooseVideo">
          <Video class="h-20 w-20 md:h-24 md:w-24" aria-hidden="true" />
          <span>Video</span>
          <strong>30s</strong>
        </button>
      </div>

      <div v-if="viewState === 'photo-camera' || viewState === 'video-camera' || viewState === 'video-recording'" class="mt-8">
        <div class="camera-preview-wrap">
          <video ref="cameraPreview" class="camera-preview" autoplay muted playsinline aria-label="Kamera Vorschau" />
          <div v-if="viewState === 'video-recording'" class="recording-pill" aria-label="Videoaufnahme laeuft">
            <span />
            {{ seconds }}s / {{ MAX_VIDEO_SECONDS }}s
          </div>
        </div>

        <div v-if="viewState === 'video-recording'" class="mt-4 h-5 overflow-hidden rounded-full bg-emerald-100">
          <div class="h-full rounded-full bg-emerald-600 transition-all" :style="{ width: `${videoProgress}%` }" />
        </div>

        <div class="mt-6 flex flex-wrap justify-center gap-4">
          <button
            v-if="viewState === 'photo-camera'"
            class="focus-ring camera-main-button bg-sky-700 text-white"
            type="button"
            aria-label="Foto jetzt aufnehmen"
            @click="capturePhoto"
          >
            <Camera class="h-16 w-16" aria-hidden="true" />
          </button>
          <button
            v-if="viewState === 'video-camera'"
            class="focus-ring camera-main-button bg-rose-600 text-white"
            type="button"
            aria-label="Video starten"
            @click="startVideoRecording"
          >
            <Circle class="h-16 w-16 fill-white" aria-hidden="true" />
          </button>
          <button
            v-if="viewState === 'video-recording'"
            class="focus-ring camera-main-button bg-rose-700 text-white"
            type="button"
            aria-label="Video stoppen"
            @click="stopVideoRecording"
          >
            <Square class="h-16 w-16 fill-white" aria-hidden="true" />
          </button>
          <button class="focus-ring camera-secondary-button" type="button" aria-label="Neu waehlen" @click="resetAll">
            <RotateCcw class="h-9 w-9" aria-hidden="true" />
          </button>
        </div>
      </div>

      <div v-if="viewState === 'photo-edit'" class="mt-8">
        <div class="photo-sticker-stage">
          <img :src="photoUrl" alt="Aufgenommenes Foto" />
          <span v-if="selectedStickers.includes('glasses')" class="sticker-overlay sticker-glasses" aria-hidden="true">
            <i /><i /><b />
          </span>
          <span v-if="selectedStickers.includes('moustache')" class="sticker-overlay sticker-moustache" aria-hidden="true">
            <i /><i />
          </span>
          <span v-if="selectedStickers.includes('necklace')" class="sticker-overlay sticker-necklace" aria-hidden="true">
            <i v-for="dot in 11" :key="dot" />
          </span>
        </div>

        <div class="mt-6 grid gap-3 sm:grid-cols-3">
          <button
            v-for="sticker in stickerOptions"
            :key="sticker.key"
            class="focus-ring sticker-choice"
            :class="{ 'sticker-choice-active': selectedStickers.includes(sticker.key) }"
            type="button"
            :aria-label="`${sticker.label} Sticker`"
            @click="toggleSticker(sticker.key)"
          >
            <span :class="sticker.className" aria-hidden="true"><i /><i /><b /></span>
            {{ sticker.label }}
          </button>
        </div>

        <div class="mt-6 grid gap-3 sm:grid-cols-2">
          <button class="focus-ring flex min-h-20 items-center justify-center gap-3 rounded-[24px] bg-white px-5 text-xl font-black text-heart-ink shadow-sm" type="button" @click="choosePhoto">
            <RotateCcw class="h-8 w-8" aria-hidden="true" />
            Neu
          </button>
          <button class="focus-ring flex min-h-20 items-center justify-center gap-3 rounded-[24px] bg-heart-leaf px-5 text-xl font-black text-white shadow-sm" type="button" @click="sendPhoto">
            <Send class="h-9 w-9" aria-hidden="true" />
            Schicken
          </button>
        </div>
      </div>

      <div v-if="viewState === 'video-preview'" class="mt-8 rounded-[28px] bg-heart-cream p-4">
        <video ref="videoPreview" class="max-h-[34rem] w-full rounded-[24px] bg-black" controls :src="videoUrl" aria-label="Video Vorschau" />
        <div class="mt-5 grid gap-3 sm:grid-cols-2">
          <button class="focus-ring flex min-h-20 items-center justify-center gap-3 rounded-[24px] bg-white px-5 text-xl font-black text-heart-ink shadow-sm" type="button" @click="chooseVideo">
            <RotateCcw class="h-8 w-8" aria-hidden="true" />
            Neu
          </button>
          <button class="focus-ring flex min-h-20 items-center justify-center gap-3 rounded-[24px] bg-heart-leaf px-5 text-xl font-black text-white shadow-sm" type="button" @click="sendVideo">
            <Send class="h-9 w-9" aria-hidden="true" />
            Schicken
          </button>
        </div>
      </div>

      <div v-if="viewState === 'sent'" class="mt-8 rounded-[28px] bg-heart-mint p-5">
        <Check class="mx-auto h-16 w-16 text-heart-leaf" aria-hidden="true" />
        <p class="mt-3 text-2xl font-black text-heart-leaf">Bei Mama oder Papa</p>
        <button class="focus-ring mt-5 min-h-16 w-full rounded-[24px] bg-heart-leaf px-5 text-xl font-black text-white" type="button" @click="returnToFriend">
          Zurueck zu {{ friendName }}
        </button>
      </div>

      <p v-if="errorText" class="mt-6 rounded-2xl bg-rose-100 px-4 py-3 text-xl font-black text-rose-800">
        {{ errorText }}
      </p>

      <p class="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-heart-cream px-4 py-2 text-sm font-black text-heart-ink">
        <Sparkles class="h-5 w-5 text-heart-coral" aria-hidden="true" />
        Video stoppt von allein nach {{ MAX_VIDEO_SECONDS }} Sekunden.
      </p>
    </div>
  </section>
</template>

<style scoped>
.camera-choice {
  display: flex;
  min-height: 14rem;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  border-radius: 2rem;
  padding: 1.5rem;
  font-size: 2rem;
  font-weight: 900;
  box-shadow: 0 18px 38px rgba(15, 23, 42, 0.08);
}

.camera-choice strong {
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.74);
  padding: 0.35rem 0.9rem;
  font-size: 1.1rem;
}

.camera-preview-wrap {
  position: relative;
  overflow: hidden;
  border-radius: 2rem;
  background: #111827;
}

.camera-preview {
  display: block;
  max-height: 34rem;
  min-height: 18rem;
  width: 100%;
  object-fit: cover;
}

.recording-pill {
  position: absolute;
  left: 1rem;
  top: 1rem;
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.94);
  padding: 0.65rem 1rem;
  color: #9f1239;
  font-size: 1.15rem;
  font-weight: 900;
}

.recording-pill span {
  width: 0.85rem;
  height: 0.85rem;
  border-radius: 999px;
  background: #e11d48;
  animation: record-blink 820ms ease-in-out infinite;
}

.camera-main-button,
.camera-secondary-button {
  display: grid;
  width: 7rem;
  height: 7rem;
  place-items: center;
  border-radius: 999px;
  box-shadow: 0 16px 34px rgba(15, 23, 42, 0.14);
}

.camera-secondary-button {
  background: #ffffff;
  color: #334155;
}

.photo-sticker-stage {
  position: relative;
  margin: 0 auto;
  max-width: 44rem;
  overflow: hidden;
  border-radius: 2rem;
  background: #fff7ed;
  box-shadow: inset 0 0 0 0.35rem rgba(251, 113, 133, 0.12);
}

.photo-sticker-stage img {
  display: block;
  width: 100%;
}

.sticker-overlay {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  pointer-events: none;
}

.sticker-glasses {
  top: 34%;
  width: 28%;
  height: 10%;
}

.sticker-glasses i {
  position: absolute;
  top: 0;
  width: 38%;
  height: 100%;
  border: 0.34rem solid #111827;
  border-radius: 0.55rem;
}

.sticker-glasses i:first-child {
  left: 0;
}

.sticker-glasses i:nth-child(2) {
  right: 0;
}

.sticker-glasses b {
  position: absolute;
  left: 38%;
  top: 46%;
  width: 24%;
  height: 0.34rem;
  border-radius: 999px;
  background: #111827;
}

.sticker-moustache {
  top: 52%;
  width: 20%;
  height: 7%;
}

.sticker-moustache i {
  position: absolute;
  top: 18%;
  width: 54%;
  height: 72%;
  border-radius: 999px 999px 999px 0;
  background: #111827;
}

.sticker-moustache i:first-child {
  left: 0;
  transform: rotate(10deg);
}

.sticker-moustache i:nth-child(2) {
  right: 0;
  transform: rotate(-10deg) scaleX(-1);
}

.sticker-necklace {
  top: 74%;
  display: flex;
  width: 34%;
  justify-content: space-between;
}

.sticker-necklace i {
  width: 0.78rem;
  height: 0.78rem;
  border-radius: 999px;
  background: #f59e0b;
}

.sticker-necklace i:nth-child(1),
.sticker-necklace i:nth-child(11) {
  margin-top: 1.2rem;
}

.sticker-necklace i:nth-child(2),
.sticker-necklace i:nth-child(10) {
  margin-top: 0.85rem;
}

.sticker-necklace i:nth-child(3),
.sticker-necklace i:nth-child(9) {
  margin-top: 0.55rem;
}

.sticker-necklace i:nth-child(4),
.sticker-necklace i:nth-child(8) {
  margin-top: 0.3rem;
}

.sticker-choice {
  display: flex;
  min-height: 5.8rem;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  border-radius: 1.5rem;
  background: #ffffff;
  padding: 1rem;
  color: #334155;
  font-size: 1.2rem;
  font-weight: 900;
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.08);
}

.sticker-choice-active {
  background: #fb7185;
  color: #ffffff;
}

.sticker-choice-glasses,
.sticker-choice-moustache,
.sticker-choice-necklace {
  position: relative;
  display: inline-block;
  width: 3.6rem;
  height: 2.3rem;
  color: currentColor;
}

.sticker-choice-glasses i {
  position: absolute;
  top: 0.45rem;
  width: 1.15rem;
  height: 0.9rem;
  border: 0.18rem solid currentColor;
  border-radius: 0.3rem;
}

.sticker-choice-glasses i:first-child {
  left: 0.35rem;
}

.sticker-choice-glasses i:nth-child(2) {
  right: 0.35rem;
}

.sticker-choice-glasses b {
  position: absolute;
  left: 1.55rem;
  top: 0.88rem;
  width: 0.5rem;
  height: 0.16rem;
  border-radius: 999px;
  background: currentColor;
}

.sticker-choice-moustache i {
  position: absolute;
  top: 0.8rem;
  width: 1.45rem;
  height: 0.65rem;
  border-radius: 999px 999px 999px 0;
  background: currentColor;
}

.sticker-choice-moustache i:first-child {
  left: 0.45rem;
  transform: rotate(10deg);
}

.sticker-choice-moustache i:nth-child(2) {
  right: 0.45rem;
  transform: rotate(-10deg) scaleX(-1);
}

.sticker-choice-moustache b {
  display: none;
}

.sticker-choice-necklace {
  display: flex;
  align-items: center;
  justify-content: space-around;
}

.sticker-choice-necklace i,
.sticker-choice-necklace b {
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 999px;
  background: currentColor;
}

@keyframes record-blink {
  0%,
  100% {
    opacity: 0.35;
  }
  50% {
    opacity: 1;
  }
}
</style>
